// Reference implementation for ALDO's default-order Shop All grid.
// Complete card, filter, sort, promotion, and analytics parity before production.

const FETCH_PAGE_SIZE = 100;
const normalizedType = (value) => String(value).trim().toLocaleLowerCase('en');

export function parsePage(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : 1;
}

// The grid now owns sort/filter state via its own `sort` and `f.<attr>` params, so
// those no longer force the native fallback. Only an explicit fallback flag or a
// free-text search query (which this browse grid does not handle) bails out.
export function hasUnsupportedState(params) {
  return [...params.keys()].some((key) => key === 'q' || key === 'balanced_fallback');
}

// URL state helpers. Filters live as repeatable `f.<attr>=value` params; sort as `sort`.
export function readState(params) {
  const filters = {};
  for (const [key, value] of params.entries()) {
    if (key.startsWith('f.') && value) {
      const attr = key.slice(2);
      (filters[attr] ||= []).push(value);
    }
  }
  // Price is a continuous range persisted as pmin / pmax (absent = unbounded on that end).
  const pmin = Number(params.get('pmin'));
  const pmax = Number(params.get('pmax'));
  const priceRange = {};
  if (params.has('pmin') && Number.isFinite(pmin)) priceRange.min = pmin;
  if (params.has('pmax') && Number.isFinite(pmax)) priceRange.max = pmax;
  return {
    sort: params.get('sort') || '',
    page: parsePage(params.get('page')),
    filters,
    priceRange: (priceRange.min != null || priceRange.max != null) ? priceRange : null,
  };
}

export function writeState(url, { sort, page, filters, priceRange }) {
  [...url.searchParams.keys()].forEach((key) => {
    if (key === 'sort' || key === 'page' || key === 'pmin' || key === 'pmax' || key.startsWith('f.')) {
      url.searchParams.delete(key);
    }
  });
  if (sort) url.searchParams.set('sort', sort);
  if (page && page > 1) url.searchParams.set('page', String(page));
  Object.entries(filters || {}).forEach(([attr, values]) => {
    values.forEach((value) => url.searchParams.append(`f.${attr}`, value));
  });
  // Only persist a bound that is actually tighter than the catalog bounds.
  if (priceRange) {
    if (Number.isFinite(priceRange.min) && (!Number.isFinite(priceRange.boundsMin) || priceRange.min > priceRange.boundsMin)) {
      url.searchParams.set('pmin', String(priceRange.min));
    }
    if (Number.isFinite(priceRange.max) && (!Number.isFinite(priceRange.boundsMax) || priceRange.max < priceRange.boundsMax)) {
      url.searchParams.set('pmax', String(priceRange.max));
    }
  }
  return url;
}

// Parse "key|value" line config from a data attribute into ordered pairs.
function parsePairs(raw) {
  return String(raw || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf('|');
      return idx === -1 ? [line, ''] : [line.slice(0, idx).trim(), line.slice(idx + 1).trim()];
    });
}

// Turn an Algolia attribute key into a readable label as a last resort.
function prettifyAttr(attr) {
  return String(attr)
    .replace(/^meta\.custom\./, '')
    .replace(/^options\./, '')
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Arabic labels for the SHARED facet attributes that have no _ar variant (size, heel
// height, price). On the Arabic storefront these override the English label map so the
// headings read in Arabic. Other facets already use their own _ar attribute.
const BPLP_FACET_LABELS_AR = {
  'options.size': 'المقاس',
  'meta.custom.heel_height': 'ارتفاع الكعب',
  'price': 'السعر',
};
// Resolve a facet heading label: Arabic override first (when the page is Arabic), then
// the configured label map, then a prettified fallback.
function facetLabel(attr, labels) {
  const isArabic = document.documentElement.lang === 'ar';
  if (isArabic && BPLP_FACET_LABELS_AR[attr]) return BPLP_FACET_LABELS_AR[attr];
  return (labels && labels[attr]) || prettifyAttr(attr);
}

// Build a facetFilters payload (array of OR-groups) from selected filter state.
// Each attribute is its own group so multiple values within an attribute are ORed
// and different attributes are ANDed — standard disjunctive refinement.
function buildFacetFilters(filters) {
  return Object.entries(filters || {})
    .filter(([, values]) => values && values.length)
    .map(([attr, values]) => values.map((value) => `${attr}:${value}`));
}

// The price facet is a continuous range, not discrete values, so it is filtered with
// numericFilters (price >= min AND price <= max) rather than facetFilters. Only emit a
// bound when it is tighter than the full catalog range, so a full-range selection adds
// no constraint. Returns an array of numeric filter expressions (ANDed by Algolia).
export const PRICE_ATTR = 'price';
function buildNumericFilters(priceRange) {
  if (!priceRange) return [];
  const out = [];
  if (Number.isFinite(priceRange.min) && (!Number.isFinite(priceRange.boundsMin) || priceRange.min > priceRange.boundsMin)) {
    out.push(`${PRICE_ATTR} >= ${priceRange.min}`);
  }
  if (Number.isFinite(priceRange.max) && (!Number.isFinite(priceRange.boundsMax) || priceRange.max < priceRange.boundsMax)) {
    out.push(`${PRICE_ATTR} <= ${priceRange.max}`);
  }
  return out;
}

export function validateMix(raw) {
  if (!raw || raw.version !== 1 || raw.enabled !== true) throw new Error('Mix is disabled or invalid');
  if (!Number.isInteger(raw.pageSize) || raw.pageSize < 1 || raw.pageSize > 100) throw new Error('Invalid page size');
  if (!Array.isArray(raw.groups) || raw.groups.length < 1 || raw.groups.length > 20) throw new Error('Use 1–20 groups');
  const keys = new Set();
  const types = new Set();
  let sum = 0;
  for (const group of raw.groups) {
    if (!group || typeof group.key !== 'string' || !/^[a-z0-9_-]+$/.test(group.key)) throw new Error('Invalid group key');
    // Accept the "*" sentinel: it means "no product_type filter — return all types in
    // Algolia's own order" (single-list ranked mode). Any other value must still be a
    // trimmed non-empty string.
    if (group.productType !== '*' && (typeof group.productType !== 'string' || !group.productType.trim() || group.productType !== group.productType.trim())) throw new Error('Invalid product type');
    if (!Number.isInteger(group.weight) || group.weight <= 0) throw new Error('Invalid weight');
    if (keys.has(group.key) || types.has(normalizedType(group.productType))) throw new Error('Duplicate group');
    keys.add(group.key);
    types.add(normalizedType(group.productType));
    sum += group.weight;
  }
  if (sum !== 100) throw new Error('Weights must total 100');
  // Optional: sale PLP flag. When true the grid restricts every query to on-sale
  // variant records and filters card swatches to on-sale colours only.
  if (raw.saleOnly != null && typeof raw.saleOnly !== 'boolean') throw new Error('saleOnly must be a boolean');
  // Optional: merch tiers flag (single-category collections only, see merchTiersApply).
  if (raw.merchTiers != null && typeof raw.merchTiers !== 'boolean') throw new Error('merchTiers must be a boolean');
  return raw;
}

// ── Merch tiers ────────────────────────────────────────────────────────────
// Client tiers for single-category PLPs, expressed as scored optionalFilters so Algolia
// ranks them in its "filters" criterion (ahead of custom ranking on an empty query).
// Separate entries add up; the season entry is an OR group, which scores its best
// match once.
//   Footwear:  core size (2) + new season (1) → 3/2/1/0 = tiers 1-2 / 3-4 / 5 / 6
//   Others:    new season (1)                 → 1/0     = tiers 1-2 / 3-4
// Full price above discount inside a score comes from the index's custom ranking
// (compare_at_price asc: 0 = not on sale sorts first). distinct then keeps each
// colour's best in-stock size, so a colour lands in its best tier exactly once.
const ATTR_PATTERN = /^[A-Za-z0-9_.]+$/;

// Mixed collections and the "*" ranked mode never get tiers, so their queries are
// unchanged.
export function merchTiersApply(mix) {
  return mix.merchTiers === true && mix.groups.length === 1 && mix.groups[0].productType !== '*';
}

// custom.season is single-line text and can hold several codes ("SS26, FW26"), while
// Algolia matches whole facet values. So keep every value present in the collection
// that contains a current season. Values starting with "-" would read as negations.
export function newSeasonValues(facetValues, seasons) {
  const current = new Set(String(seasons || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean));
  return facetValues.filter((value) => !String(value).startsWith('-') &&
    String(value).split(',').some((s) => current.has(s.trim().toUpperCase())));
}

export function buildMerchOptionalFilters(productType, { coreAttr, seasonAttr, seasonValues }) {
  const out = [];
  if (normalizedType(productType) === 'footwear' && ATTR_PATTERN.test(coreAttr || '')) {
    out.push(`${coreAttr}:true<score=2>`);
  }
  if (ATTR_PATTERN.test(seasonAttr || '') && seasonValues?.length) {
    out.push(seasonValues.map((value) => `${seasonAttr}:${value}<score=1>`));
  }
  return out.length ? out : undefined;
}

// Generates a fair, interleaved page. Replays preceding pages so offsets remain
// correct when one group runs out and its slots are given to the other groups.
export function planPage(counts, weights, pageSize, pageNumber) {
  if (!Array.isArray(counts) || counts.length !== weights.length ||
      counts.some((n) => !Number.isInteger(n) || n < 0) ||
      weights.some((n) => !Number.isInteger(n) || n <= 0) ||
      !Number.isInteger(pageSize) || pageSize < 1 ||
      !Number.isInteger(pageNumber) || pageNumber < 1) throw new Error('Invalid page plan');

  const used = counts.map(() => 0);
  let offsets = used.slice();
  let slots = [];
  for (let page = 1; page <= pageNumber; page += 1) {
    offsets = used.slice();
    slots = [];
    const credit = counts.map(() => 0);
    for (let position = 0; position < pageSize; position += 1) {
      const active = counts.map((count, i) => (used[i] < count ? i : -1)).filter((i) => i >= 0);
      if (!active.length) break;
      const totalWeight = active.reduce((total, i) => total + weights[i], 0);
      for (const i of active) credit[i] += weights[i];
      let chosen = active[0];
      for (const i of active.slice(1)) if (credit[i] > credit[chosen]) chosen = i;
      credit[chosen] -= totalWeight;
      used[chosen] += 1;
      slots.push(chosen);
    }
  }
  const lengths = counts.map((_, i) => slots.filter((slot) => slot === i).length);
  return { offsets, lengths, slots };
}

function escapeFacet(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function createSearch(appId, apiKey, indexName) {
  const url = `https://${appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(indexName)}/query`;
  return async (params, signal) => {
    const timeout = new AbortController();
    const abort = () => timeout.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-algolia-application-id': appId,
        'x-algolia-api-key': apiKey,
      },
      body: JSON.stringify({ query: '', analytics: false, ...params }),
      signal: timeout.signal,
    });
    if (!response.ok) throw new Error(`Algolia request failed (${response.status})`);
    return await response.json();
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  };
}

export function exactCount(result) {
  // Algolia approximates nbHits for large result sets (exhaustiveNbHits:false) as a
  // performance optimization; the number can still be correct. We therefore no longer
  // require exhaustiveNbHits, but we KEEP the pagination-coverage guard below: nbPages
  // must cover nbHits so every record the plan expects is actually retrievable. If a
  // category grows past paginationLimitedTo, this still throws instead of silently
  // truncating. readRange's expectedCount check still catches mid-request index changes.
  if (!Number.isSafeInteger(result.nbHits) || result.nbHits < 0) throw new Error('Exact Algolia counts required');
  if (result.hitsPerPage !== 1 || !Number.isSafeInteger(result.nbPages) || result.nbPages < result.nbHits) {
    throw new Error('Algolia pagination limit does not cover the whole category');
  }
  return result.nbHits;
}

export async function readRange(search, filters, start, length, signal, expectedCount) {
  if (!length) return [];
  const firstPage = Math.floor(start / FETCH_PAGE_SIZE);
  const lastPage = Math.floor((start + length - 1) / FETCH_PAGE_SIZE);
  const pages = await Promise.all(
    Array.from({ length: lastPage - firstPage + 1 }, (_, i) =>
      search({ filters, page: firstPage + i, hitsPerPage: FETCH_PAGE_SIZE }, signal)
    )
  );
  // Count-stability guard. The authoritative exact count was already established by
  // exactCount() on the hitsPerPage:1 count query (which enforced exhaustiveNbHits:true),
  // and is passed in as expectedCount. This range query uses hitsPerPage:100, and Algolia
  // reports an APPROXIMATE count (exhaustiveNbHits:false) for larger paginated result sets
  // even when the value is correct — e.g. a 1032-hit category returns nbHits:1032 with
  // exhaustiveNbHits:false. We therefore require the VALUE to still equal expectedCount
  // (which catches a genuine mid-request index change) but do NOT require this query's own
  // exhaustiveness flag. The slice-length check below remains the backstop that every
  // needed record was actually retrieved.
  if (pages.some((result) => !Number.isSafeInteger(result.nbHits) || result.nbHits !== expectedCount)) {
    throw new Error('Index counts changed; reload the collection');
  }
  const hits = pages.flatMap((result) => result.hits || []);
  const slice = hits.slice(start - firstPage * FETCH_PAGE_SIZE, start - firstPage * FETCH_PAGE_SIZE + length);
  if (slice.length !== length) throw new Error('Algolia pagination limit or index changed during the request');
  return slice;
}

function textElement(tag, className, value) {
  const element = document.createElement(tag);
  element.className = className;
  element.textContent = value == null ? '' : String(value);
  return element;
}

export function assembleHits(plan, ranges, groups, collectionId) {
  const positions = ranges.map(() => 0);
  const seen = new Set();
  return plan.slots.map((groupIndex) => {
    const hit = ranges[groupIndex][positions[groupIndex]++];
    // For a "*" (ranked, all-types) group the hits are intentionally mixed types, so
    // skip ONLY the product_type equality guard. The objectID, duplicate-objectID, and
    // collection-membership guards still apply.
    const typeOk = groups[groupIndex].productType === '*' ||
      normalizedType(hit?.product_type) === normalizedType(groups[groupIndex].productType);
    if (!hit?.objectID || seen.has(String(hit.objectID)) || !typeOk ||
        !hit.collection_ids?.some((id) => String(id) === collectionId)) {
      throw new Error('Duplicate or unexpected record in category response');
    }
    seen.add(String(hit.objectID));
    return hit;
  });
}

// ── Swatches ───────────────────────────────────────────────────────────────
// color_swatch_map is indexed on the hit as a JSON string listing every color of
// the style (regardless of stock). We render one swatch per color, then resolve
// real per-color stock/price/image from /products/{handle}.js (same source the
// existing ALDO app-block hook uses). Algolia's distinct on style_color_color_code
// means a hit represents one color; the swatch map lets the card show its siblings.
const MAX_VISIBLE_SWATCHES = 4;
const variantCache = new Map();

function normalizeColor(value) {
  return String(value || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/[-_]/g, ' ');
}

function parseSwatchMap(raw) {
  if (!raw) return [];
  let data = raw;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch { return []; }
  }
  if (Array.isArray(data)) data = data[0];
  if (!data?.swatches || !Array.isArray(data.swatches)) return [];
  const seen = new Set();
  return data.swatches
    .filter((s) => s.color && s.shopifyImageUrl && !seen.has(s.color) && seen.add(s.color))
    .map((s) => ({ color: s.color, image: s.shopifyImageUrl, productCode: s.productCode || '' }));
}

async function fetchVariants(handle) {
  if (variantCache.has(handle)) return variantCache.get(handle);
  const promise = fetch(`/products/${encodeURIComponent(handle)}.js`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  variantCache.set(handle, promise);
  return promise;
}

// Shopify's /products/{handle}.js reports prices in cents; Algolia hit.price is in
// major units. Keep both paths consistent by formatting from a major-unit number.
function formatMoney(amountMajor, currency) {
  // Always format prices in English (Latin digits + "AED" code), even on the Arabic
  // storefront, per brand requirement.
  const format = new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'code' });
  return format.format(Number(amountMajor));
}

// Resolve, for each swatch color, the representative variant and its availability.
// With a size option we prefer an in-stock variant for that color; this is how an
// out-of-stock color ends up marked unavailable (and skipped on click).
function resolveSwatchVariants(product, swatchEls) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const hasSize = (product?.options || []).some((opt) => {
    const name = typeof opt === 'string' ? opt : opt?.name;
    return String(name || '').toLowerCase() === 'size';
  });
  swatchEls.forEach((el) => {
    const color = normalizeColor(el.dataset.color);
    if (!color) return;
    const matches = variants.filter((v) =>
      normalizeColor(v.option1) === color || normalizeColor(v.option2) === color);
    if (!matches.length) { el.dataset.available = 'false'; return; }
    const chosen = hasSize ? (matches.find((v) => v.available) || matches[0]) : matches[0];
    el.dataset.variantId = String(chosen.id || '');
    el.dataset.available = chosen.available ? 'true' : 'false';
    el.dataset.price = chosen.price != null ? String(chosen.price) : '';
    el.dataset.compareAtPrice = chosen.compare_at_price != null ? String(chosen.compare_at_price) : '';
    el.dataset.image = chosen.featured_image?.src || el.dataset.image || '';
  });
}

function renderCard(hit, currency, rootUrl, options) {
  const saleOnly = options?.saleOnly === true;
  const li = document.createElement('li');
  li.className = 'balanced-plp__card';
  li.dataset.productType = hit.product_type;
  li.dataset.objectId = hit.objectID;

  const link = document.createElement('a');
  link.className = 'balanced-plp__card-link aldo-product-link';
  const variant = encodeURIComponent(hit.objectID);
  if (!hit.handle || hit.price == null || !Number.isFinite(Number(hit.price))) throw new Error('Incomplete product card');
  const base = rootUrl.replace(/\/$/, '');
  link.href = `${base}/products/${encodeURIComponent(hit.handle)}?variant=${variant}`;

  // Media wrapper lets the sale badge overlay the image.
  const media = document.createElement('div');
  media.className = 'balanced-plp__card-media';
  const image = document.createElement('img');
  image.className = 'aldo-primary-image';
  // Reserve the image box before load (ALDO product images share a 296:385 portrait).
  // Setting intrinsic width/height lets the browser hold the correct space so cards
  // don't jump/grow when the image finally downloads — no fixed CSS aspect-ratio needed.
  image.width = 296;
  image.height = 385;
  if (hit.image) { image.src = hit.image; image.alt = hit.title || ''; image.loading = 'lazy'; }
  media.append(image);

  // Sale badge: shown when the selected variant has a compare-at price above its price.
  // Seeded from the hit; wireSwatches updates it when the shopper switches colors.
  const badge = document.createElement('span');
  badge.className = 'balanced-plp__badge';
  const onSale = hit.compare_at_price != null && Number(hit.compare_at_price) > Number(hit.price);
  badge.textContent = document.documentElement.lang === 'ar' ? 'تخفيض' : 'Sale';
  badge.hidden = !onSale;
  media.append(badge);
  link.append(media);

  // Name line: "Name - SEO text" in grey, matching the design. The SEO text comes from
  // the silhouette metafield (localized), the same source the main PLP card uses.
  const isArabic = document.documentElement.lang === 'ar';
  const seoText = isArabic
    ? (hit.meta?.custom?.silhouette_ar || hit.meta?.custom?.silhouette)
    : hit.meta?.custom?.silhouette;
  const title = document.createElement('h3');
  title.className = 'balanced-plp__card-title';
  const nameSpan = document.createElement('span');
  nameSpan.className = 'balanced-plp__card-name';
  // In Arabic, use the arabic_title metafield (fall back to the default title).
  const displayTitle = isArabic
    ? (hit.meta?.custom?.arabic_title || hit.title)
    : hit.title;
  nameSpan.textContent = displayTitle || '';
  title.append(nameSpan);
  if (seoText) {
    const seoSpan = document.createElement('span');
    seoSpan.className = 'balanced-plp__card-seo';
    seoSpan.textContent = ` - ${capitalizeWords(seoText)}`;
    title.append(seoSpan);
  }
  link.append(title);

  // Price + compare-at, updated in place when a swatch is selected.
  const priceWrap = document.createElement('p');
  priceWrap.className = 'balanced-plp__card-price';
  const priceEl = textElement('span', 'balanced-plp__price-current', formatMoney(Number(hit.price), currency));
  const compareEl = document.createElement('span');
  compareEl.className = 'balanced-plp__price-compare';
  if (hit.compare_at_price != null && Number(hit.compare_at_price) > Number(hit.price)) {
    compareEl.textContent = formatMoney(Number(hit.compare_at_price), currency);
  }
  priceWrap.append(priceEl, compareEl);
  link.append(priceWrap);
  li.append(link);

  // Swatches. Pre-fill the indexed color (hit.option1) from the hit so it is
  // usable before the variant fetch resolves the rest. On the Arabic storefront use
  // the Arabic swatch map, falling back to the default map if it is not indexed.
  const swatchMapRaw = document.documentElement.lang === 'ar'
    ? (hit.meta?.custom?.color_swatch_map_ar || hit.meta?.custom?.color_swatch_map)
    : hit.meta?.custom?.color_swatch_map;
  const swatches = parseSwatchMap(swatchMapRaw);
  if (swatches.length) {
    const indexedColor = normalizeColor(hit.option1);
    const grid = document.createElement('div');
    grid.className = 'balanced-plp__swatches';
    swatches.forEach((swatch) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'balanced-plp__swatch';
      item.dataset.color = swatch.color;
      item.dataset.image = swatch.image;
      item.dataset.productCode = swatch.productCode;
      item.setAttribute('aria-label', swatch.color);
      const isIndexed = normalizeColor(swatch.color) === indexedColor;
      if (isIndexed) {
        item.dataset.available = hit.inventory_available ? 'true' : 'false';
        item.dataset.price = hit.price != null ? String(hit.price) : '';
        item.dataset.compareAtPrice = hit.compare_at_price != null ? String(hit.compare_at_price) : '';
      }
      const img = document.createElement('img');
      img.src = swatch.image;
      img.alt = swatch.color;
      img.width = 28; img.height = 28; img.loading = 'lazy';
      item.append(img);
      grid.append(item);
    });
    li.append(grid);
    // Defer the per-card variant fetch until the card is near the viewport, so a page
    // of 30 cards does not fire 30 /products/{handle}.js requests up front.
    observeForSwatches(li, () => wireSwatches(li, hit, currency, base, { saleOnly, keepHitVariant: options?.keepHitVariant === true }));
  }

  return li;
}

// Shared lazy-init observer: runs each card's swatch wiring when it approaches view.
// Falls back to immediate wiring if IntersectionObserver is unavailable.
let swatchObserver = null;
const swatchInit = new WeakMap();
function observeForSwatches(li, init) {
  if (typeof IntersectionObserver === 'undefined') { init(); return; }
  if (!swatchObserver) {
    swatchObserver = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        const fn = swatchInit.get(entry.target);
        if (fn) { swatchInit.delete(entry.target); fn(); }
      });
    }, { rootMargin: '400px 0px' });
  }
  swatchInit.set(li, init);
  swatchObserver.observe(li);
}

// Fetches variant stock, marks each swatch's availability/price/image, applies the
// 4-visible + overflow rule, selects the hit's own color by default, and wires clicks
// that swap image/price/link in place (unavailable swatches jump to the next available).
function wireSwatches(li, hit, currency, base, options) {
  const saleOnly = options?.saleOnly === true;
  const isArabic = document.documentElement.lang === 'ar';
  const image = li.querySelector('.aldo-primary-image');
  const productLink = li.querySelector('.aldo-product-link');
  const priceEl = li.querySelector('.balanced-plp__price-current');
  const compareEl = li.querySelector('.balanced-plp__price-compare');
  const badge = li.querySelector('.balanced-plp__badge');
  const grid = li.querySelector('.balanced-plp__swatches');

  fetchVariants(hit.handle).then((product) => {
    if (!li.isConnected || !product) return;
    let items = Array.from(grid.querySelectorAll('.balanced-plp__swatch'));
    resolveSwatchVariants(product, items);

    // On a sale PLP, mark colours whose chosen variant is NOT actually discounted as
    // unavailable so the shared removal below drops them. /products/{handle}.js has no
    // on_sale flag, so sale state is recomputed here as compare_at_price > price
    // (prices are in cents from that endpoint). This keeps the swatch strip to colours
    // the shopper can actually buy on sale, matching the sale-filtered grid.
    if (saleOnly) {
      items.forEach((s) => {
        const price = Number(s.dataset.price || 0);
        const compare = Number(s.dataset.compareAtPrice || 0);
        if (!(compare > price)) s.dataset.available = 'false';
      });
    }

    // Drop out-of-stock colors entirely: a swatch is kept only if at least one of
    // its variants is purchasable (data-available === 'true', from variant.available).
    // On a sale PLP this also drops non-discounted colours (marked above).
    items.forEach((s) => { if (s.dataset.available !== 'true') s.remove(); });
    items = Array.from(grid.querySelectorAll('.balanced-plp__swatch'));

    // Pick the swatch for THIS card's own color. Out-of-stock colors never appear as
    // hits (Algolia hides them), so the card's own color is always in stock and present
    // among `items`. Match on the exact style-color code first (immune to label
    // differences like "Dark Green" vs "Green"), then the color label, then first.
    const hitCode = String(hit.meta?.custom?.style_color_color_code || '').toUpperCase();
    const hitColor = normalizeColor(hit.option1);
    const defaultSwatch =
      (hitCode && items.find((s) => String(s.dataset.productCode || '').toUpperCase() === hitCode)) ||
      (hitColor && items.find((s) => normalizeColor(s.dataset.color) === hitColor)) ||
      items[0];

    // Merch tiers: the hit is the variant Algolia ranked (an in-stock core size), so the
    // card's own colour links to it rather than to the colour's first in-stock size.
    if (options?.keepHitVariant === true && defaultSwatch) {
      const ranked = product.variants?.find((v) => String(v.id) === String(hit.objectID) && v.available);
      const swatchColor = normalizeColor(defaultSwatch.dataset.color);
      if (ranked && (normalizeColor(ranked.option1) === swatchColor || normalizeColor(ranked.option2) === swatchColor)) {
        defaultSwatch.dataset.variantId = String(ranked.id);
        defaultSwatch.dataset.price = ranked.price != null ? String(ranked.price) : '';
        defaultSwatch.dataset.compareAtPrice = ranked.compare_at_price != null ? String(ranked.compare_at_price) : '';
        defaultSwatch.dataset.image = ranked.featured_image?.src || defaultSwatch.dataset.image || '';
      }
    }

    // Keep the selected swatch visible: move it to the front so it is never buried
    // inside the +N overflow when there are more than MAX_VISIBLE_SWATCHES colors.
    if (defaultSwatch && grid.firstElementChild !== defaultSwatch) {
      grid.insertBefore(defaultSwatch, grid.firstElementChild);
      items = Array.from(grid.querySelectorAll('.balanced-plp__swatch'));
    }

    // Overflow: keep up to 4 swatches visible, collapse the rest into a
    // non-clickable +N indicator (N = remaining available swatches).
    grid.querySelector('.balanced-plp__swatch-more')?.remove();
    items.forEach((s, i) => { s.style.display = i >= MAX_VISIBLE_SWATCHES ? 'none' : ''; });
    const extra = items.length - MAX_VISIBLE_SWATCHES;
    if (extra > 0) {
      const more = document.createElement('span');
      more.className = 'balanced-plp__swatch-more';
      more.textContent = `+${extra}`;
      grid.append(more);
    }

    const activate = (swatch) => {
      if (!swatch) return;
      items.forEach((s) => s.classList.remove('is-active'));
      swatch.classList.add('is-active');
      if (image && swatch.dataset.image) { image.src = swatch.dataset.image; image.srcset = swatch.dataset.image; }
      if (productLink && swatch.dataset.variantId) {
        // `base` (routes.root_url) already carries the /ar locale prefix in Arabic, so
        // don't add it again — that produced /ar/ar/products/...
        productLink.href = `${base}/products/${encodeURIComponent(hit.handle)}?variant=${swatch.dataset.variantId}`;
      }
      // /products/{handle}.js prices are in cents; convert to major units.
      if (priceEl && swatch.dataset.price) {
        priceEl.textContent = formatMoney(Number(swatch.dataset.price) / 100, currency);
      }
      const compare = Number(swatch.dataset.compareAtPrice || 0);
      const current = Number(swatch.dataset.price || 0);
      const onSale = compare > current;
      if (compareEl) compareEl.textContent = onSale ? formatMoney(compare / 100, currency) : '';
      if (badge) badge.hidden = !onSale;
    };

    activate(defaultSwatch);

    // Every remaining swatch is in stock, so a click just selects it. The visible
    // set stays stable on click; only the initial default is moved to the front.
    items.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        activate(item);
      });
    });
  });
}

// Build facetFilters (array of OR-groups) excluding one attribute. Used for disjunctive
// counts: when counting a refined facet's values, we must omit that facet's own filter.
function facetFiltersExcluding(filters, exceptAttr) {
  return Object.entries(filters || {})
    .filter(([attr, values]) => attr !== exceptAttr && values && values.length)
    .map(([attr, values]) => values.map((value) => `${attr}:${value}`));
}

// ── Facets (disjunctive) ───────────────────────────────────────────────────
// Collection-wide facet values + counts (whole eligible collection, not one category
// stream). Disjunctive refinement: for a facet the shopper is ALREADY refining, its own
// filter must be excluded when counting its values, so the other options stay visible
// and selectable (multi-select within a facet). We therefore run:
//   • one base query applying all active filters → counts for un-refined facets
//   • one query per refined facet applying all filters EXCEPT that facet's own →
//     counts for that facet's own values
// Results are merged so each facet's counts come from the query that omits its own filter.
async function fetchFacets(search, baseFilter, filters, facetList, signal, priceRange) {
  // Always request the price attribute too, so Algolia returns facet stats (min/max)
  // for the slider's bounds. Requesting a numeric attribute as a facet yields its stats.
  const requested = facetList && facetList.length ? [...facetList] : ['*'];
  if (!requested.includes('*') && !requested.includes(PRICE_ATTR)) requested.push(PRICE_ATTR);
  const refinedAttrs = Object.keys(filters || {}).filter((a) => (filters[a] || []).length);
  const priceNumeric = buildNumericFilters(priceRange);

  // Base query: all filters applied (including the price range). Supplies counts for
  // facets with no active selection. Price-facet STATS, however, must reflect the full
  // range so the slider bounds don't collapse to the current selection — so the price
  // stats come from the price-excluded query below, not this one.
  const baseResult = await search({
    filters: baseFilter,
    facets: requested,
    facetFilters: buildFacetFilters(filters),
    numericFilters: priceNumeric,
    maxValuesPerFacet: 100,
    hitsPerPage: 0,
  }, signal);
  const merged = { ...(baseResult.facets || {}) };

  // Price slider bounds: query with every filter EXCEPT the price range so the handle
  // bounds stay the full collection range (disjunctive treatment of the price facet).
  let priceStats = baseResult.facets_stats?.[PRICE_ATTR] || null;
  if (priceNumeric.length) {
    const priceBoundsResult = await search({
      filters: baseFilter,
      facets: [PRICE_ATTR],
      facetFilters: buildFacetFilters(filters),
      maxValuesPerFacet: 1,
      hitsPerPage: 0,
    }, signal);
    priceStats = priceBoundsResult.facets_stats?.[PRICE_ATTR] || priceStats;
  }

  if (!refinedAttrs.length) return { facets: merged, priceStats };

  // One query per refined facet, excluding that facet's own filter. We only need that
  // facet back, so request just it to keep the response small. The price range stays
  // applied here (it is not one of the discrete refined facets). This keeps a facet's
  // OWN values visible after selecting one of them (multi-select), while facets the
  // shopper has not refined are counted against the current selection — so their values
  // that would yield no results are hidden rather than shown as unusable options.
  const perFacet = await Promise.all(refinedAttrs.map((attr) =>
    search({
      filters: baseFilter,
      facets: [attr],
      facetFilters: facetFiltersExcluding(filters, attr),
      numericFilters: priceNumeric,
      maxValuesPerFacet: 100,
      hitsPerPage: 0,
    }, signal)
  ));
  refinedAttrs.forEach((attr, i) => {
    const values = perFacet[i].facets?.[attr];
    if (values) merged[attr] = values; // override with disjunctive counts for this facet
  });
  return { facets: merged, priceStats };
}

// Render facet groups into the container. `facets` is Algolia's { attr: { value: count } }.
// labels/order come from section config; selected reflects current filter state.
// Dual-handle price range slider. `stats` is Algolia's facet stats { min, max } for the
// price attribute (whole eligible collection). `priceRange` is the current selection.
// Calls onChange({ min, max, boundsMin, boundsMax }) when the shopper commits a change.
function renderPriceSlider(group, heading, stats, priceRange, currency, onChange) {
  const isArabic = document.documentElement.lang === 'ar';
  const boundsMin = Math.floor(Number(stats.min));
  const boundsMax = Math.ceil(Number(stats.max));
  // Degenerate range (all items same price): nothing to slide.
  if (!Number.isFinite(boundsMin) || !Number.isFinite(boundsMax) || boundsMax <= boundsMin) return false;

  let selMin = Number.isFinite(priceRange?.min) ? Math.max(boundsMin, priceRange.min) : boundsMin;
  let selMax = Number.isFinite(priceRange?.max) ? Math.min(boundsMax, priceRange.max) : boundsMax;
  if (selMin > selMax) { selMin = boundsMin; selMax = boundsMax; }

  // Use currencyDisplay:'code' so the currency always renders as the code text (e.g.
  // "AED 250") rather than a symbol like "$", matching the design.
  const fmt = (n) => {
    const label = new Intl.NumberFormat('en', {
      style: 'currency', currency, currencyDisplay: 'code', maximumFractionDigits: 0,
    }).format(n);
    return n >= boundsMax ? `${label}+` : label;
  };

  const wrap = document.createElement('div');
  wrap.className = 'balanced-plp__price-values';

  // Live summary next to the heading, e.g. "$0 - $250+".
  const summary = document.createElement('span');
  summary.className = 'balanced-plp__price-summary';
  heading.append(summary);

  // Currency code used as a static prefix inside each input (e.g. "AED").
  const currencyPrefix = (() => {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency', currency, currencyDisplay: 'code', maximumFractionDigits: 0,
    }).formatToParts(0);
    return (parts.find((p) => p.type === 'currency')?.value || currency).trim();
  })();

  // Editable min/max number inputs. The shopper can type a price; values stay in sync
  // with the handles and commit (re-query) on change/blur/Enter.
  const labels = document.createElement('div');
  labels.className = 'balanced-plp__price-labels';

  const makeField = (which) => {
    const field = document.createElement('div');
    field.className = `balanced-plp__price-field balanced-plp__price-field--${which}`;
    const prefix = document.createElement('span');
    prefix.className = 'balanced-plp__price-prefix';
    prefix.textContent = currencyPrefix;
    prefix.setAttribute('aria-hidden', 'true');
    const input = document.createElement('input');
    input.type = 'number';
    input.inputMode = 'numeric';
    input.className = 'balanced-plp__price-number';
    input.min = String(boundsMin);
    input.max = String(boundsMax);
    input.step = '1';
    input.setAttribute('aria-label',
      which === 'min'
        ? (isArabic ? 'أدنى سعر' : 'Minimum price')
        : (isArabic ? 'أعلى سعر' : 'Maximum price'));
    field.append(prefix, input);
    return { field, input };
  };

  const minField = makeField('min');
  const maxField = makeField('max');
  const minLabel = minField.input;
  const maxLabel = maxField.input;
  labels.append(minField.field, maxField.field);

  // Two overlaid range inputs form the dual handle. A track element shows the
  // selected span between them.
  const track = document.createElement('div');
  track.className = 'balanced-plp__price-track';
  const fill = document.createElement('div');
  fill.className = 'balanced-plp__price-fill';
  track.append(fill);

  const minInput = document.createElement('input');
  minInput.type = 'range';
  minInput.className = 'balanced-plp__price-input balanced-plp__price-input--min';
  minInput.min = String(boundsMin);
  minInput.max = String(boundsMax);
  minInput.step = '1';
  minInput.setAttribute('aria-label', isArabic ? 'أدنى سعر' : 'Minimum price');

  const maxInput = document.createElement('input');
  maxInput.type = 'range';
  maxInput.className = 'balanced-plp__price-input balanced-plp__price-input--max';
  maxInput.min = String(boundsMin);
  maxInput.max = String(boundsMax);
  maxInput.step = '1';
  maxInput.setAttribute('aria-label', isArabic ? 'أعلى سعر' : 'Maximum price');

  track.append(minInput, maxInput);
  wrap.append(labels, track);

  const span = boundsMax - boundsMin;
  const paint = () => {
    minInput.value = String(selMin);
    maxInput.value = String(selMax);
    const leftPct = ((selMin - boundsMin) / span) * 100;
    const rightPct = ((selMax - boundsMin) / span) * 100;
    const isRtl = document.documentElement.dir === 'rtl'
      || getComputedStyle(document.documentElement).direction === 'rtl';
    // Use explicit left + width so the purple fill always renders (logical insets can
    // behave inconsistently on absolutely-positioned elements). Flip for RTL.
    const widthPct = Math.max(0, rightPct - leftPct);
    fill.style.left = isRtl ? 'auto' : `${leftPct}%`;
    fill.style.right = isRtl ? `${leftPct}%` : 'auto';
    fill.style.width = `${widthPct}%`;
    // Don't clobber the field the shopper is currently typing in.
    if (document.activeElement !== minLabel) minLabel.value = String(selMin);
    if (document.activeElement !== maxLabel) maxLabel.value = String(selMax);
    summary.textContent = `${fmt(selMin)} - ${fmt(selMax)}`;
  };

  // Keep the two handles from crossing.
  const clamp = () => {
    selMin = Math.min(Number(minInput.value), selMax);
    selMax = Math.max(Number(maxInput.value), selMin);
  };

  // Raise whichever handle is being used so overlapping thumbs never get stuck.
  const raise = (active, other) => { active.style.zIndex = '4'; other.style.zIndex = '3'; };
  minInput.addEventListener('pointerdown', () => raise(minInput, maxInput));
  maxInput.addEventListener('pointerdown', () => raise(maxInput, minInput));

  minInput.addEventListener('input', () => { selMin = Math.min(Number(minInput.value), selMax); paint(); });
  maxInput.addEventListener('input', () => { selMax = Math.max(Number(maxInput.value), selMin); paint(); });

  // Commit (re-query) only when the shopper releases the handle, not on every pixel.
  const commit = () => {
    clamp();
    onChange({ min: selMin, max: selMax, boundsMin, boundsMax });
  };
  minInput.addEventListener('change', commit);
  maxInput.addEventListener('change', commit);

  // ── Editable number fields ──
  // Read a typed value, clamp it into the valid range (and against the other handle),
  // sync the slider, and commit. An empty min means boundsMin; empty max means boundsMax.
  const commitFromFields = () => {
    const rawMin = minLabel.value.trim();
    const rawMax = maxLabel.value.trim();
    let nextMin = rawMin === '' ? boundsMin : Math.round(Number(rawMin));
    let nextMax = rawMax === '' ? boundsMax : Math.round(Number(rawMax));
    if (!Number.isFinite(nextMin)) nextMin = boundsMin;
    if (!Number.isFinite(nextMax)) nextMax = boundsMax;
    // Clamp to catalog bounds.
    nextMin = Math.min(Math.max(nextMin, boundsMin), boundsMax);
    nextMax = Math.min(Math.max(nextMax, boundsMin), boundsMax);
    // Keep min <= max (swap if the shopper inverts them).
    if (nextMin > nextMax) { const t = nextMin; nextMin = nextMax; nextMax = t; }
    selMin = nextMin;
    selMax = nextMax;
    paint();
    onChange({ min: selMin, max: selMax, boundsMin, boundsMax });
  };

  [minLabel, maxLabel].forEach((input) => {
    input.addEventListener('change', commitFromFields);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') { event.preventDefault(); input.blur(); }
    });
  });

  paint();
  group.append(wrap);
  return true;
}

// Capitalize each word of a facet value so the list reads consistently
// (e.g. "ANKLE BOOTS", "cross body", "Boots" all render as "Ankle Boots").
function capitalizeWords(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

// Arabic → English colour name map, mirroring the main PLP's colorMap, so the swatch
// image class (swatch-Black, swatch-Red, …) resolves in both locales.
const BPLP_COLOR_MAP = {
  'متعدد الألوان': 'Multi', 'أحمر': 'Red', 'بيج': 'Beige', 'بني': 'Brown',
  'أسود': 'Black', 'وردي': 'Pink', 'أزرق': 'Blue', 'ذهبي': 'Gold',
  'أبيض': 'White', 'فضي': 'Silver', 'أصفر': 'Yellow', 'رمادي': 'Gray',
  'بنفسجي': 'Purple', 'برتقالي': 'Orange', 'أخضر': 'Green',
  'متنوع': 'Assorted', 'بدون لون': 'No Color',
};
// Resolve a colour value to the asset swatch class name used in algolia-custom-hooks
// (.swatch-Black, .swatch-Red, …). Arabic values are mapped; English passed through.
function colorSwatchClass(value) {
  const mapped = BPLP_COLOR_MAP[String(value).trim()] || capitalizeWords(value);
  return `swatch-${mapped.replace(/\s+/g, '-')}`;
}

function renderFacets(container, facets, { labels, order, filters, onToggle, priceAttr, priceStats, priceRange, currency, onPriceChange, expanded }) {
  // `expanded` is a Set of attr keys the shopper has opened; it persists across
  // re-renders so toggling a value does not collapse the accordion the user is in.
  const openSet = expanded instanceof Set ? expanded : new Set();
  container.replaceChildren();
  const attrs = Object.keys(facets);
  // Price is driven by its own stats (min/max), not a facet value map, so it should
  // render even when `facets` has no discrete entries for it.
  const ordered = order && order.length
    ? order.filter((a) => attrs.includes(a) || a === priceAttr)
    : attrs;

  ordered.forEach((attr) => {
    const isPrice = attr === priceAttr;
    const values = facets[attr];
    if (!isPrice) {
      const valueCount = values ? Object.keys(values).length : 0;
      if (valueCount === 0) return; // empty facet auto-hides
      // A single-value facet can't refine anything (every result already has that value),
      // so hide it — UNLESS the shopper has it actively selected, in which case keep it
      // visible so they can see and clear their refinement.
      const hasSelection = (filters[attr] || []).length > 0;
      if (valueCount < 2 && !hasSelection) return;
    }
    if (isPrice && (!priceStats || !Number.isFinite(Number(priceStats.min)) || !Number.isFinite(Number(priceStats.max)))) return;

    const group = document.createElement('div');
    group.className = 'balanced-plp__facet';
    group.dataset.attr = attr;

    const isOpen = openSet.has(attr);

    const heading = document.createElement('button');
    heading.type = 'button';
    heading.className = 'balanced-plp__facet-heading';
    heading.setAttribute('aria-expanded', String(isOpen));
    const headingLabel = document.createElement('span');
    headingLabel.className = 'balanced-plp__facet-heading-label';
    headingLabel.textContent = facetLabel(attr, labels);
    heading.append(headingLabel);
    // Selected values summary shown beside the header while collapsed.
    const selectedSummary = document.createElement('span');
    selectedSummary.className = 'balanced-plp__facet-selected';
    heading.append(selectedSummary);
    group.append(heading);

    if (isPrice) {
      const added = renderPriceSlider(group, heading, priceStats, priceRange, currency, onPriceChange);
      if (!added) return; // degenerate range — skip the facet entirely
      const sliderWrap = group.querySelector('.balanced-plp__price-values');
      if (sliderWrap) sliderWrap.hidden = !isOpen;
      // Price summary when collapsed. Fall back a missing end to the catalog bounds so
      // it never prints NaN before the stats resolve.
      if (priceRange && (priceRange.min != null || priceRange.max != null)) {
        const cur = currency || 'AED';
        const money = (val) => new Intl.NumberFormat('en', {
          style: 'currency', currency: cur, currencyDisplay: 'code', maximumFractionDigits: 0,
        }).format(val);
        const sMin = Number.isFinite(priceRange.min) ? priceRange.min : priceRange.boundsMin;
        const sMax = Number.isFinite(priceRange.max) ? priceRange.max : priceRange.boundsMax;
        if (Number.isFinite(sMin) && Number.isFinite(sMax)) {
          selectedSummary.textContent = `${money(sMin)} - ${money(sMax)}`;
        }
      }
      heading.addEventListener('click', () => {
        const open = heading.getAttribute('aria-expanded') === 'true';
        heading.setAttribute('aria-expanded', String(!open));
        if (open) openSet.delete(attr); else openSet.add(attr);
        if (sliderWrap) sliderWrap.hidden = open;
      });
      container.append(group);
      return;
    }

    // Facet type → render style: color = image swatches, size = tiles, else = checkbox.
    const isColor = /color_family/.test(attr);
    const isSize = attr === 'options.size';
    group.classList.toggle('balanced-plp__facet--color', isColor);
    group.classList.toggle('balanced-plp__facet--size', isSize);

    const list = document.createElement('div');
    list.className = 'balanced-plp__facet-values';
    if (isColor) list.classList.add('balanced-plp__facet-values--color');
    if (isSize) list.classList.add('balanced-plp__facet-values--size');
    list.hidden = !isOpen; // restore expanded state across re-renders

    const selected = new Set(filters[attr] || []);
    // Selected values summary beside the header (capitalized), shown while collapsed.
    if (selected.size) {
      selectedSummary.textContent = Array.from(selected).map(capitalizeWords).join(', ');
    }
    // Sort values alphabetically, with numeric values (e.g. sizes "6", "6.5", "10")
    // ordered numerically ascending rather than as strings ("10" before "6").
    const collator = new Intl.Collator(document.documentElement.lang || 'en', {
      numeric: true,
      sensitivity: 'base',
    });
    const entries = Object.entries(values).sort((a, b) => collator.compare(a[0], b[0]));

    entries.forEach(([value, count]) => {
      const id = `facet-${attr}-${value}`.replace(/[^a-z0-9_-]/gi, '-');
      const label = document.createElement('label');
      label.className = 'balanced-plp__facet-value';
      if (isColor) label.classList.add('balanced-plp__facet-value--color', colorSwatchClass(value));
      if (isSize) label.classList.add('balanced-plp__facet-value--size');
      if (selected.has(value)) label.classList.add('is-selected');
      label.htmlFor = id;

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.id = id;
      input.checked = selected.has(value);
      input.addEventListener('change', () => onToggle(attr, value, input.checked));

      // Colour facets show an image swatch (styled via .swatch-{Name}::after); size and
      // standard facets just use the text label.
      let swatch = null;
      if (isColor) {
        swatch = document.createElement('span');
        swatch.className = 'balanced-plp__swatch-dot';
        swatch.setAttribute('aria-hidden', 'true');
      }

      const text = document.createElement('span');
      text.className = 'balanced-plp__facet-label';
      // Display values capitalized consistently (actual filter value is unchanged).
      text.textContent = capitalizeWords(value);
      const countEl = document.createElement('span');
      countEl.className = 'balanced-plp__facet-count';
      countEl.textContent = count;

      if (isColor) label.append(input, swatch, text);
      else label.append(input, text, countEl);
      list.append(label);
    });

    heading.addEventListener('click', () => {
      const open = heading.getAttribute('aria-expanded') === 'true';
      heading.setAttribute('aria-expanded', String(!open));
      if (open) openSet.delete(attr); else openSet.add(attr);
      list.hidden = open;
    });

    group.append(list);
    container.append(group);
  });

  if (!container.children.length) {
    const empty = document.createElement('p');
    empty.className = 'balanced-plp__facet-empty';
    empty.textContent = document.documentElement.lang === 'ar' ? 'لا توجد مرشحات' : 'No filters available';
    container.append(empty);
  }
}

// Render the sort menu from configured options. The active option is marked; selecting
// one calls onSort(indexName) where '' means the default balanced mix.
function renderSortMenu(menu, options, activeIndex, onSort) {
  // Render options into the dedicated options container (keeps the header intact).
  const optionsWrap = menu.querySelector('.balanced-plp__sort-options') || menu;
  optionsWrap.replaceChildren();
  const menuTarget = optionsWrap;
  options.forEach(([label, indexName]) => {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'balanced-plp__sort-option';
    item.setAttribute('role', 'menuitemradio');
    const isActive = (indexName || '') === (activeIndex || '');
    item.setAttribute('aria-checked', String(isActive));
    if (isActive) item.classList.add('is-active');

    // Radio circle (filled when active) + label, matching the Figma Sort By radios.
    const radio = document.createElement('span');
    radio.className = 'balanced-plp__sort-radio';
    radio.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'balanced-plp__sort-text';
    text.textContent = label;
    item.append(radio, text);

    item.addEventListener('click', () => onSort(indexName || ''));
    menuTarget.append(item);
  });
}

// Skeleton placeholders shown while products load, matching the grid card footprint.
function renderSkeletons(count) {
  const frag = document.createDocumentFragment();
  for (let i = 0; i < count; i += 1) {
    const li = document.createElement('li');
    li.className = 'balanced-plp__card balanced-plp__card--skeleton';
    li.setAttribute('aria-hidden', 'true');
    li.innerHTML =
      '<span class="balanced-plp__sk balanced-plp__sk-media"></span>' +
      '<span class="balanced-plp__sk balanced-plp__sk-line balanced-plp__sk-line--title"></span>' +
      '<span class="balanced-plp__sk balanced-plp__sk-line balanced-plp__sk-line--price"></span>' +
      '<span class="balanced-plp__sk balanced-plp__sk-swatches"></span>';
    frag.append(li);
  }
  return frag;
}

// Load-more control: a single button that requests the next page. It is hidden when
// the current page is the last one. onMore receives the next page number.
function renderLoadMore(container, page, pageCount, onMore) {
  container.replaceChildren();
  if (page >= pageCount) return;
  const isArabic = document.documentElement.lang === 'ar';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'balanced-plp__load-more';
  button.textContent = isArabic ? 'تحميل المزيد' : 'Load more';
  button.addEventListener('click', () => onMore(page + 1));
  container.append(button);
}

// Renders a plain global-sorted grid (no quota) for an explicit sort. Pages accumulate
// like the balanced view. Returns { total, pageCount } for the status/load-more.
async function renderGlobalPage(ctx, page, append) {
  const { sortedSearch, baseFilter, filters, priceRange, collectionId, app, root, grid, signal, pageSize, saleOnly } = ctx;
  const facetFilters = buildFacetFilters(filters);
  const numericFilters = buildNumericFilters(priceRange);
  const firstToRender = append ? page : 1;
  let total = 0;
  const allCards = [];
  for (let p = firstToRender; p <= page; p += 1) {
    const result = await sortedSearch({
      filters: baseFilter,
      facetFilters,
      numericFilters,
      hitsPerPage: pageSize,
      page: p - 1,
    }, signal);
    if (ctx.isStale()) return null;
    total = Number.isSafeInteger(result.nbHits) ? result.nbHits : 0;
    const cards = (result.hits || [])
      .filter((hit) => hit?.objectID && hit.collection_ids?.some((id) => String(id) === collectionId))
      .map((hit) => renderCard(hit, app.store_currency || 'AED', root.dataset.rootUrl || '/', { saleOnly }));
    allCards.push(...cards);
  }
  grid.querySelectorAll('.balanced-plp__card--skeleton').forEach((el) => el.remove());
  if (append) grid.append(...allCards);
  else grid.replaceChildren(...allCards);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return { total, pageCount };
}

async function mount() {
  const root = document.getElementById('balanced-plp');
  if (!root || hasUnsupportedState(new URL(location.href).searchParams)) return;
  const grid = root.querySelector('.balanced-plp__grid');
  const status = root.querySelector('.balanced-plp__status');
  const pagination = root.querySelector('.balanced-plp__pagination');
  const facetsContainer = root.querySelector('.balanced-plp__facets');
  const filtersPanel = root.querySelector('.balanced-plp__filters');
  const filterTrigger = root.querySelector('.balanced-plp__filter-trigger');
  const filtersClose = root.querySelector('.balanced-plp__filters-close');
  const filtersClear = root.querySelector('.balanced-plp__filters-clear');
  const filtersApply = root.querySelector('.balanced-plp__filters-apply');
  const sortTrigger = root.querySelector('.balanced-plp__sort-trigger');
  const sortMenu = root.querySelector('.balanced-plp__sort-menu');
  const sortClose = root.querySelector('.balanced-plp__sort-close');
  const sortOverlay = root.querySelector('.balanced-plp__sort-overlay');
  const overlay = root.querySelector('.balanced-plp__overlay');
  const fallback = document.querySelector('[id$="__product-grid"]');

  let controller;
  let requestId = 0;
  let cachedCounts = null;
  let countsAt = 0;

  // Live state (seeded from the URL).
  const state = readState(new URL(location.href).searchParams);
  const initialPage = state.page;

  // Config from the section data attributes.
  const isArabic = document.documentElement.lang === 'ar';
  const sortOptions = parsePairs(root.dataset.sortOptions);
  const sortOptionList = sortOptions.length ? sortOptions : [['Relevance', '']];
  const facetLabels = Object.fromEntries(parsePairs(root.dataset.facetLabels));
  // Language-specific allow-list: English shows the EN facet attributes, Arabic the AR
  // ones, so the _ar/_en pairs are never both visible. This is also the display order.
  const facetOrderRaw = isArabic ? root.dataset.facetOrderAr : root.dataset.facetOrderEn;
  const facetOrder = parsePairs(facetOrderRaw).map(([a]) => a);
  // Request exactly the allow-listed facets (not ['*']). Algolia still returns only the
  // values that exist for this collection, so empty facets auto-hide per collection.
  const facetList = facetOrder.length ? facetOrder : undefined;

  try {
    const mix = validateMix(JSON.parse(root.dataset.mix));
    const app = window.algoliaShopify?.config;
    if (!app?.app_id || !app?.search_api_key || !app?.index_prefix) throw new Error('Algolia storefront config unavailable');
    const collectionId = root.dataset.collectionId;
    if (!/^\d+$/.test(collectionId || '')) throw new Error('Collection ID unavailable');
    const defaultIndex = root.dataset.indexName?.trim() || `${app.index_prefix}products`;
    const search = createSearch(app.app_id, app.search_api_key, defaultIndex);
    const collectionFilter = `collection_ids:"${collectionId}"`;
    // Sale PLP: restrict to variant records that carry a compare-at price. Algolia
    // filters can't compare two attributes (compare_at_price > price), so we use the
    // expressible `compare_at_price > 0` form here. Because records are per-variant,
    // this keeps only on-sale variant records; distinct then yields one card per product
    // whose representative is on sale. (compare_at_price must be set to "filter only" in
    // the index's attributesForFaceting.) The swatch strip is further narrowed to truly
    // discounted colours client-side in wireSwatches (compare_at_price > price).
    const saleOnly = mix.saleOnly === true;
    const saleClause = saleOnly ? ' AND compare_at_price > 0' : '';
    // Confirm the live PLP's stock/visibility clauses before production.
    const baseFilter = `${collectionFilter} AND inventory_available:true${saleClause}`;
    const weights = mix.groups.map((group) => group.weight);

    // Merch tiers (single-category collections with plp_mix.merchTiers only). The
    // season values are looked up once per page load from the whole collection; if the
    // lookup fails the page still ranks by core size.
    const merchActive = merchTiersApply(mix);
    const merchSeasonAttr = root.dataset.merchSeasonAttr?.trim() || '';
    let merchSeasonValues = null;
    async function merchOptionalFilters(signal) {
      if (!merchActive) return undefined;
      if (merchSeasonValues === null) {
        try {
          const result = ATTR_PATTERN.test(merchSeasonAttr)
            ? await search({ filters: baseFilter, hitsPerPage: 0, facets: [merchSeasonAttr], maxValuesPerFacet: 1000 }, signal)
            : {};
          merchSeasonValues = newSeasonValues(Object.keys(result.facets?.[merchSeasonAttr] || {}), root.dataset.merchSeasons);
        } catch (error) {
          if (signal.aborted) throw error;
          console.warn('[balanced-plp] season lookup failed; ranking by core size only', error);
          merchSeasonValues = [];
        }
      }
      return buildMerchOptionalFilters(mix.groups[0].productType, {
        coreAttr: root.dataset.merchCoreAttr?.trim(),
        seasonAttr: merchSeasonAttr,
        seasonValues: merchSeasonValues,
      });
    }

    // Group filters include the shopper's selected facet values (ANDed to each
    // category stream so a card can never bypass the collection/stock constraints) and
    // the price range as numericFilters.
    function currentGroupFilters() {
      const facetFilters = buildFacetFilters(state.filters);
      const numericFilters = buildNumericFilters(state.priceRange);
      return mix.groups.map((group) => ({
        // "*" = single-list ranked mode: no product_type split, so the group's base
        // query returns all types in Algolia's default ranked order (a matching Algolia
        // Rule's pins show through). Shopper facet/numeric filters still apply below.
        filters: group.productType === '*'
          ? baseFilter
          : `${baseFilter} AND product_type:"${escapeFacet(group.productType)}"`,
        facetFilters,
        numericFilters,
      }));
    }

    // Persistent set of expanded facet attrs, so a value toggle (which re-renders the
    // facet list) doesn't collapse the accordion the shopper is working in.
    const expandedFacets = new Set();

    // Refresh collection-wide facet counts for the drawer (one ungrouped query).
    async function refreshFacets(signal) {
      try {
        const { facets, priceStats } = await fetchFacets(
          search, baseFilter, state.filters, facetList, signal, state.priceRange);
        if (signal.aborted) return;
        // Seed the slider bounds onto the current price range so buildNumericFilters can
        // tell a full-range selection (= no constraint) from a real one.
        if (priceStats && state.priceRange) {
          const bMin = Math.floor(Number(priceStats.min));
          const bMax = Math.ceil(Number(priceStats.max));
          state.priceRange.boundsMin = bMin;
          state.priceRange.boundsMax = bMax;
          // On reload only the tightened bound is in the URL; backfill the other end from
          // the catalog bounds so the chip/summary never show NaN.
          if (!Number.isFinite(state.priceRange.min)) state.priceRange.min = bMin;
          if (!Number.isFinite(state.priceRange.max)) state.priceRange.max = bMax;
          // Re-render the active-filter chips now that the bounds are known.
          renderActiveFilters();
        }
        renderFacets(facetsContainer, facets, {
          labels: facetLabels,
          order: facetOrder,
          filters: state.filters,
          priceAttr: PRICE_ATTR,
          priceStats,
          priceRange: state.priceRange,
          currency: app.store_currency || 'AED',
          expanded: expandedFacets,
          onPriceChange: ({ min, max, boundsMin, boundsMax }) => {
            // A selection equal to the full bounds means "no price filter" — clear it.
            if (min <= boundsMin && max >= boundsMax) {
              state.priceRange = null;
            } else {
              state.priceRange = { min, max, boundsMin, boundsMax };
            }
            state.page = 1;
            cachedCounts = null; // price narrows the balanced counts; drop the cache
            apply(true);
          },
          onToggle: (attr, value, checked) => {
            const list = state.filters[attr] || [];
            state.filters[attr] = checked
              ? [...new Set([...list, value])]
              : list.filter((v) => v !== value);
            if (!state.filters[attr].length) delete state.filters[attr];
            state.page = 1;
            // Counts are per filtered balanced query; changing a filter invalidates the
            // cached unfiltered counts, so reset them (same as on a sort change) before
            // re-querying. Otherwise the plan is built from stale counts and readRange's
            // exact-count guard throws "Index counts changed".
            cachedCounts = null;
            apply(true);
          },
        });
      } catch (error) {
        console.error('[balanced-plp] facets', error);
      }
    }

    // One-time check that product_type is a usable facet. Runs once per session, in
    // parallel with the first count query, instead of serially before every refresh.
    let facetProbeDone = false;
    async function probeFacetOnce(signal) {
      if (facetProbeDone) return;
      const probe = await search({ filters: baseFilter, hitsPerPage: 1 }, signal);
      if (probe.nbHits > 0) {
        const sampleType = probe.hits?.[0]?.product_type;
        if (!sampleType) throw new Error('Indexed product_type is missing');
        const filteredProbe = await search({
          filters: `${baseFilter} AND product_type:"${escapeFacet(sampleType)}"`,
          hitsPerPage: 0,
        }, signal);
        if (filteredProbe.nbHits === 0) throw new Error('Add product_type as a filterable facet in Algolia first');
      }
      facetProbeDone = true;
    }

    // Renders one visible page of the BALANCED mix (default sort). append=false
    // rebuilds from page 1; append=true adds the next page's cards.
    async function showBalanced(pageNumber, append, signal, current) {
      if (!cachedCounts || Date.now() - countsAt > 60000) {
        const gf = currentGroupFilters();
        // Probe (once) and counts fire concurrently so the probe never blocks counts.
        const [, ...countsResult] = await Promise.all([
          probeFacetOnce(signal),
          ...gf.map((q) => search({ ...q, hitsPerPage: 1 }, signal)),
        ]);
        if (current !== requestId) return null;
        cachedCounts = countsResult.map(exactCount);
        countsAt = Date.now();
      }
      const counts = cachedCounts;
      const total = counts.reduce((sum, count) => sum + count, 0);
      const pageCount = Math.max(1, Math.ceil(total / mix.pageSize));
      const page = Math.min(parsePage(pageNumber), pageCount);
      const gf = currentGroupFilters();
      // undefined for mixed / "*" / untagged collections, so their requests are unchanged.
      const optionalFilters = await merchOptionalFilters(signal);
      if (current !== requestId) return null;
      const firstToRender = append ? page : 1;
      const allCards = [];
      for (let p = firstToRender; p <= page; p += 1) {
        const plan = planPage(counts, weights, mix.pageSize, p);
        const ranges = await Promise.all(gf.map((q, i) =>
          // optionalFilters only reorder hits, never change counts, so only page reads get them.
          readRange((params, s) => search({ ...params, facetFilters: q.facetFilters, numericFilters: q.numericFilters, optionalFilters }, s),
            q.filters, plan.offsets[i], plan.lengths[i], signal, counts[i])
        ));
        if (current !== requestId) return null;
        const pageCards = assembleHits(plan, ranges, mix.groups, collectionId)
          .map((hit) => renderCard(hit, app.store_currency || 'AED', root.dataset.rootUrl || '/', { saleOnly, keepHitVariant: optionalFilters !== undefined }));
        allCards.push(...pageCards);
      }
      grid.querySelectorAll('.balanced-plp__card--skeleton').forEach((el) => el.remove());
      if (append) grid.append(...allCards);
      else grid.replaceChildren(...allCards);
      return { total, pageCount, page };
    }

    // Main render entry. Chooses the balanced mix (default sort) or a global sorted
    // grid, applies filters, updates the URL, and refreshes facet counts.
    async function apply(updateUrl = true, append = false) {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      const current = ++requestId;
      // Show skeletons immediately so the grid never looks empty while querying.
      const skeletonCount = mix.pageSize;
      if (append) {
        grid.append(renderSkeletons(skeletonCount));
      } else {
        status.textContent = document.documentElement.lang === 'ar' ? 'جارٍ التحميل…' : 'Loading products…';
        grid.replaceChildren(renderSkeletons(skeletonCount));
        root.hidden = false;
        if (fallback) fallback.hidden = true;
      }
      try {
        let outcome;
        if (!state.sort) {
          outcome = await showBalanced(state.page, append, signal, current);
        } else {
          // Explicit global sort: drop the quota, query the chosen replica directly.
          const sortedSearch = createSearch(app.app_id, app.search_api_key, state.sort);
          outcome = await renderGlobalPage({
            sortedSearch, baseFilter, filters: state.filters, priceRange: state.priceRange,
            collectionId, app, root, grid, saleOnly,
            signal, pageSize: mix.pageSize, isStale: () => current !== requestId,
          }, state.page, append);
        }
        if (current !== requestId || !outcome) return;

        const page = outcome.page ?? state.page;
        state.page = page;
        const shown = grid.querySelectorAll('.balanced-plp__card:not(.balanced-plp__card--skeleton)').length;
        // Product count, matching the design: "404 Results" (total, not page count).
        const total = outcome.total;
        status.textContent = shown
          ? (document.documentElement.lang === 'ar'
              ? (total === 1 ? 'نتيجة واحدة' : `${total} نتيجة`)
              : (total === 1 ? '1 Result' : `${total} Results`))
          : (document.documentElement.lang === 'ar' ? 'لا توجد منتجات' : 'No results');
        updateFilterChrome(outcome.total);
        renderLoadMore(pagination, page, outcome.pageCount, (target) => { state.page = target; apply(true, true); });
        renderSortMenu(sortMenu, sortOptionList, state.sort, (indexName) => {
          if (indexName === state.sort) { closeSort(); return; }
          state.sort = indexName;
          state.page = 1;
          cachedCounts = null; // counts are per balanced query; reset on sort change
          closeSort();
          apply(true);
        });
        root.hidden = false;
        if (fallback) fallback.hidden = true;
        if (!append) refreshFacets(signal);

        const url = writeState(new URL(location.href), state);
        history[updateUrl ? 'pushState' : 'replaceState']({ balancedPage: page }, '', url);
      } catch (error) {
        if (signal.aborted || current !== requestId) return;
        cachedCounts = null;
        if (fallback) fallback.hidden = state.page !== initialPage;
        grid.replaceChildren();
        pagination.replaceChildren();
        status.textContent = (document.documentElement.lang === 'ar'
          ? 'العرض المختلط غير متاح. ' : 'The mixed view is unavailable. ');
        const link = document.createElement('a');
        const url = new URL(location.href);
        url.searchParams.set('balanced_fallback', '1');
        link.href = url.href;
        link.textContent = document.documentElement.lang === 'ar'
          ? 'افتح عرض المجموعة القياسي' : 'Open the standard collection view';
        status.append(link);
        root.hidden = false;
        console.error('[balanced-plp]', error);
      }
    }

    // Updates the drawer title "Filters (#)" and the Apply button label with the number
    // of active filter values, and reflects the result count on the Apply button.
    const filtersTitleEl = root.querySelector('.balanced-plp__filters-title');
    const filterCountEl = root.querySelector('.balanced-plp__filter-count');
    const filtersResultsEl = root.querySelector('.balanced-plp__filters-results');
    const activeFiltersEl = root.querySelector('.balanced-plp__active-filters');
    const isAr = document.documentElement.lang === 'ar';
    function activeFilterCount() {
      const discrete = Object.values(state.filters).reduce((sum, values) => sum + (values?.length || 0), 0);
      return discrete + (state.priceRange ? 1 : 0);
    }

    // Human label for a chip. Facet labels come from the configured label map.
    function facetDisplayLabel(attr) {
      return facetLabels[attr] || prettifyAttr(attr);
    }

    // Render the active-filter chips row: one "× value" chip per selected facet value,
    // plus one for the price range. Clicking a chip removes that refinement and re-queries.
    function renderActiveFilters() {
      if (!activeFiltersEl) return;
      activeFiltersEl.replaceChildren();

      const makeChip = (text, onRemove, modifier) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'balanced-plp__chip' + (modifier ? ` ${modifier}` : '');
        const x = document.createElement('span');
        x.className = 'balanced-plp__chip-x';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '\u00d7'; // ×
        const label = document.createElement('span');
        label.className = 'balanced-plp__chip-label';
        label.textContent = text;
        chip.append(x, label);
        chip.setAttribute('aria-label',
          (isAr ? 'إزالة الفلتر: ' : 'Remove filter: ') + text);
        chip.addEventListener('click', onRemove);
        return chip;
      };

      // Discrete facet value chips. Normalize ALL-CAPS backend values to Title Case.
      Object.entries(state.filters).forEach(([attr, vals]) => {
        (vals || []).forEach((value) => {
          activeFiltersEl.append(makeChip(capitalizeWords(value), () => {
            state.filters[attr] = (state.filters[attr] || []).filter((v) => v !== value);
            if (!state.filters[attr].length) delete state.filters[attr];
            state.page = 1;
            cachedCounts = null;
            apply(true);
          }));
        });
      });

      // Price range chip.
      if (state.priceRange) {
        const cur = app.store_currency || 'AED';
        const money = (n) => new Intl.NumberFormat('en', {
          style: 'currency', currency: cur, currencyDisplay: 'code', maximumFractionDigits: 0,
        }).format(n);
        // On reload only the tightened bound is in the URL (e.g. just pmax when the min
        // was left at the catalog minimum), so the other end can be undefined until the
        // facet stats load. Fall back to the catalog bounds; if those aren't known yet,
        // skip that end rather than printing "NaN".
        const pr = state.priceRange;
        const min = Number.isFinite(pr.min) ? pr.min : pr.boundsMin;
        const max = Number.isFinite(pr.max) ? pr.max : pr.boundsMax;
        const label = (Number.isFinite(min) ? money(min) : '') +
          ' - ' +
          (Number.isFinite(max) ? money(max) : '');
        activeFiltersEl.append(makeChip(label.trim().replace(/^-\s*|\s*-$/g, '').trim() || money(max ?? min), () => {
          state.priceRange = null;
          state.page = 1;
          cachedCounts = null;
          apply(true);
        }, 'balanced-plp__chip--price'));
      }
    }

    function updateFilterChrome(total) {
      const n = activeFilterCount();
      if (filtersTitleEl) {
        const base = isAr ? 'المرشحات' : 'Filters';
        filtersTitleEl.textContent = n ? `${base} (${n})` : base;
      }
      // Toolbar "Filters" button count badge (circled number). When filters are applied
      // the filter icon is hidden and only the count badge shows beside the label.
      if (filterCountEl) {
        filterCountEl.textContent = n ? String(n) : '';
        filterCountEl.hidden = !n;
      }
      if (filterTrigger) filterTrigger.classList.toggle('has-filters', n > 0);
      // Drawer header "Results: N items".
      if (filtersResultsEl && Number.isFinite(total)) {
        filtersResultsEl.textContent = isAr
          ? `النتائج: ${total}`
          : `Results: ${total} item${total === 1 ? '' : 's'}`;
      }
      renderActiveFilters();
      // Footer button reflects the number of applied filters, not the product count.
      if (filtersApply) {
        if (n > 0) {
          filtersApply.textContent = isAr
            ? `تطبيق ${n} ${n === 1 ? 'فلتر' : 'فلاتر'}`
            : `Apply ${n} ${n === 1 ? 'Filter' : 'Filters'}`;
        } else {
          filtersApply.textContent = isAr ? 'تطبيق' : 'Apply';
        }
      }
    }

    // ── Controls: open/close drawer + sort menu ──
    const openFilters = () => {
      filtersPanel.hidden = false;
      overlay.hidden = false;
      filterTrigger.setAttribute('aria-expanded', 'true');
      root.classList.add('balanced-plp--filters-open');
    };
    const closeFilters = () => {
      filtersPanel.hidden = true;
      overlay.hidden = true;
      filterTrigger.setAttribute('aria-expanded', 'false');
      root.classList.remove('balanced-plp--filters-open');
      // Collapse all facet accordions so they start closed next time the drawer opens.
      expandedFacets.clear();
      root.querySelectorAll('.balanced-plp__facet-heading[aria-expanded="true"]').forEach((h) => {
        h.setAttribute('aria-expanded', 'false');
        const grp = h.closest('.balanced-plp__facet');
        grp?.querySelector('.balanced-plp__facet-values')?.setAttribute('hidden', '');
        grp?.querySelector('.balanced-plp__price-values')?.setAttribute('hidden', '');
      });
    };
    const closeSort = () => {
      sortMenu.hidden = true;
      if (sortOverlay) sortOverlay.hidden = true;
      sortTrigger.setAttribute('aria-expanded', 'false');
    };
    const openSort = () => {
      sortMenu.hidden = false;
      if (sortOverlay) sortOverlay.hidden = false;
      sortTrigger.setAttribute('aria-expanded', 'true');
    };
    filterTrigger.addEventListener('click', () =>
      (filtersPanel.hidden ? openFilters() : closeFilters()));
    filtersClose?.addEventListener('click', closeFilters);
    overlay?.addEventListener('click', closeFilters);
    filtersClear?.addEventListener('click', () => {
      state.filters = {};
      state.priceRange = null; // clearing also resets the price range
      state.page = 1;
      cachedCounts = null; // clearing filters changes the counts; drop the cache
      apply(true);
    });
    // Filters apply live as they are toggled, so Apply just closes the drawer.
    filtersApply?.addEventListener('click', closeFilters);
    sortTrigger.addEventListener('click', () =>
      (sortMenu.hidden ? openSort() : closeSort()));
    sortClose?.addEventListener('click', closeSort);
    sortOverlay?.addEventListener('click', closeSort);
    document.addEventListener('click', (event) => {
      if (!sortMenu.hidden
          && !event.target.closest('.balanced-plp__sort')
          && !event.target.closest('.balanced-plp__sort-overlay')) closeSort();
    });

    // ── Sticky toolbar border ──
    // The toolbar sticks at top:var(--header-height) so it never leaves a gap and never
    // jumps to the very top — it pins exactly where the sticky header ends. We only add
    // the bottom border once it is actually stuck. A zero-height sentinel just above the
    // toolbar drives this: a scroll handler compares the toolbar's top against the live
    // header height (read from --header-height each time, so it stays correct if the
    // header resizes). Using the real header height — not a hardcoded 108px — removes the
    // jump that happened when the two values disagreed.
    const toolbar = root.querySelector('.balanced-plp__toolbar');
    if (toolbar) {
      const headerHeight = () => {
        const raw = getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height').trim();
        const n = parseFloat(raw);
        return Number.isFinite(n) ? n : 0;
      };
      const updateStuck = () => {
        // Stuck when the toolbar's top has reached (or passed) the header's bottom edge.
        const top = toolbar.getBoundingClientRect().top;
        toolbar.classList.toggle('is-stuck', Math.round(top) <= Math.round(headerHeight()) + 1);
      };
      updateStuck();
      window.addEventListener('scroll', updateStuck, { passive: true });
      window.addEventListener('resize', updateStuck, { passive: true });
    }

    // ── Grid view toggle (2-col / 1-col) ──
    // Switches the grid's column count via a modifier class; no re-query needed.
    const gridButtons = root.querySelectorAll('.balanced-plp__grid-btn');
    gridButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const cols = btn.dataset.cols;
        grid.classList.toggle('balanced-plp__grid--one-col', cols === '1');
        gridButtons.forEach((b) => {
          const active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', String(active));
        });
      });
    });

    await apply(false);
    window.addEventListener('popstate', () => {
      const params = new URL(location.href).searchParams;
      if (hasUnsupportedState(params)) { location.reload(); return; }
      const next = readState(params);
      state.sort = next.sort;
      state.page = next.page;
      state.filters = next.filters;
      state.priceRange = next.priceRange;
      cachedCounts = null;
      apply(false);
    });
  } catch (error) {
    if (fallback) fallback.hidden = false;
    root.hidden = true;
    console.error('[balanced-plp]', error);
  }
}

if (typeof document !== 'undefined') mount();
